import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from "@angular/core";

import { BaseComponent, UserService, EventBusService } from "@mypxplat/xplat/core";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { format, getDaysInMonth, set } from "date-fns";

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-date-picker",
  templateUrl: "date-picker.component.html",
  standalone: false,
})
export class DatePickerComponent extends BaseComponent implements OnInit {
  public years: Array<string> = [];
  public months: Array<string> = ["January", "February", "March", "April", "May", "June", "July ", "August", "September", "October", "November", "December"];
  public days: Array<string> = [];

  @Input() initWithDate: Date = new Date();
  @Input() earliestYear: number = 1969;
  @Input() latestYear: number;
  @Input() includeTimePicker: boolean = false;
  @Input() includeTimeZonePicker: boolean = false;
  public hours = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  // every 5 minutes
  public minutes = ["00", "05", "10", "15", "20", "25", "30", "35", "40", "45", "50", "55"];
  public selectedHour: any = "9";
  public selectedMinute: any = "00";
  public selectedAmPm: any = "am";
  public selectedTimezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone;
  public timezoneOptions: { value: string; label: string }[] = [];
  public selectedYear: string = new Date().getFullYear().toString();
  public selectedMonth: string = this.months[new Date().getMonth()];
  public selectedDay: string = new Date().getDate().toString();

  public prettySelectedDate: string;
  private _selectedDate: Date;
  set selectedDate(date) {
    if (date) {
      this.prettySelectedDate = format(date, "MMMM do yyyy");
      this._selectedDate = date;
    }
  }

  get selectedDate() {
    return this._selectedDate;
  }

  @Input() public past: boolean = true;
  @Output() dateSelected: EventEmitter<any> = new EventEmitter();

  constructor(
    userService: UserService,
    private _eventBus: EventBusService,
    public activeModal: NgbActiveModal
  ) {
    super(userService);
  }

  ngOnInit() {
    const dateToUse = this.initWithDate || new Date();
    if (this.includeTimeZonePicker) {
      this.selectedYear = dateToUse.getUTCFullYear().toString();
      this.selectedMonth = this.months[dateToUse.getUTCMonth()];
      this.selectedDay = dateToUse.getUTCDate().toString();
    } else {
      this.selectedYear = dateToUse.getFullYear().toString();
      this.selectedMonth = this.months[dateToUse.getMonth()];
      this.selectedDay = dateToUse.getDate().toString();
    }
    for (let i = this.earliestYear; i < (this.past ? (this.latestYear ? this.latestYear : new Date().getFullYear() + 1) : 2030); i++) {
      this.years.push(i.toString());
    }
    this.years.reverse();
    this.populateDays(parseInt(this.selectedYear), this.months.indexOf(this.selectedMonth));

    if (this.includeTimeZonePicker) {
      const zones = [
        "Pacific/Honolulu",
        "America/Anchorage",
        "America/Los_Angeles",
        "America/Denver",
        "America/Chicago",
        "America/New_York",
        "America/Sao_Paulo",
        "Europe/London",
        "Europe/Paris",
        "Europe/Berlin",
        "Europe/Rome",
        "Europe/Istanbul",
        "Asia/Dubai",
        "Asia/Kolkata",
        "Asia/Shanghai",
        "Asia/Tokyo",
        "Asia/Seoul",
        "Australia/Sydney",
        "Pacific/Auckland",
      ];
      this.timezoneOptions = zones.map((tz) => {
        const offset = this.getTimezoneOffset(tz);
        return { value: tz, label: `${tz} (UTC${offset})` };
      });
      if (!zones.includes(this.selectedTimezone)) {
        const offset = this.getTimezoneOffset(this.selectedTimezone);
        this.timezoneOptions.unshift({ value: this.selectedTimezone, label: `${this.selectedTimezone} (UTC${offset})` });
      }
    }

    if (this.includeTimePicker) {
      const hours = this.includeTimeZonePicker ? dateToUse.getUTCHours() : dateToUse.getHours();
      this.selectedAmPm = hours >= 12 ? "pm" : "am";
      this.selectedHour = (hours % 12 || 12).toString();
      const mins = this.includeTimeZonePicker ? dateToUse.getUTCMinutes() : dateToUse.getMinutes();
      const roundedMins = Math.round(mins / 5) * 5;
      this.selectedMinute = roundedMins < 10 ? "0" + roundedMins : roundedMins.toString();
    }

    this.buildSelectedDate();
  }

  select(value, type) {
    if (type == "year") {
      this.selectedYear = value;
      this.populateDays(parseInt(this.selectedYear), this.months.indexOf(this.selectedMonth));
    } else if (type == "month") {
      this.selectedMonth = value;
      this.populateDays(parseInt(this.selectedYear), this.months.indexOf(this.selectedMonth));
    } else if (type == "day") {
      this.selectedDay = value;
    }
    this.setDate();
  }

  setDate() {
    setTimeout(() => {
      this.buildSelectedDate();
    }, 10);
  }

  private buildSelectedDate() {
    let hour = parseInt(this.selectedHour);
    if (this.selectedAmPm === "pm" && hour !== 12) hour += 12;
    if (this.selectedAmPm === "am" && hour === 12) hour = 0;

    if (this.includeTimeZonePicker) {
      // Store the wall-clock values directly as UTC so they remain unchanged
      this.selectedDate = new Date(Date.UTC(parseInt(this.selectedYear), this.months.indexOf(this.selectedMonth), parseInt(this.selectedDay), hour, parseInt(this.selectedMinute), 0));
    } else {
      // Original behavior: create in local time then store as UTC
      const localDate = new Date(parseInt(this.selectedYear), this.months.indexOf(this.selectedMonth), parseInt(this.selectedDay), hour, parseInt(this.selectedMinute), 0);
      this.selectedDate = new Date(Date.UTC(localDate.getUTCFullYear(), localDate.getUTCMonth(), localDate.getUTCDate(), localDate.getUTCHours(), localDate.getUTCMinutes(), 0));
    }
  }

  private getTimezoneOffset(tz: string): string {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "shortOffset" });
    const parts = formatter.formatToParts(now);
    const offsetPart = parts.find((p) => p.type === "timeZoneName");
    return offsetPart ? offsetPart.value.replace("GMT", "") || "+0" : "";
  }

  done() {
    if (this.includeTimeZonePicker) {
      this.dateSelected.next({ date: this.selectedDate, timezone: this.selectedTimezone });
    } else {
      this.dateSelected.next(this.selectedDate);
    }
    this.close();
  }

  populateDays(year: number, month: number) {
    this.days = [];
    if (!year) year = new Date().getFullYear();
    if (!month && month !== 0) month = new Date().getMonth() + 1;
    const date = new Date(year, month, 1);
    const numberOfDays = getDaysInMonth(date);
    this.days = [];
    for (let i = 1; i <= numberOfDays; i++) {
      this.days.push(i.toString());
    }
  }

  close(event?) {
    if (event) event.stopPropagation();
    this.activeModal.close();
  }
}
